import React, { useState, useRef, useEffect } from 'react';
import { Info, X } from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import { createPortal } from 'react-dom';
import WorkInProgress from '../../components/WorkInProgress.js';

const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal-root') : null;
const OptionPortal = ({ children, style }) => {
    if (portalRoot) {
        return createPortal(
            <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
                {children}
            </div>,
            portalRoot
        );
    }
    return (
        <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
            {children}
        </div>
    );
};

const shortVariant = (v) => String(v || '').split(',')[0].trim();

export default function BulkPricing() {
    const history = useHistory();
    const location = useLocation();
    const [showWip, setShowWip] = useState(false);

    // Discard modal
    const [discard_modal_open, set_discard_modal_open] = useState(false);
    const discard_ref = useRef(null);
    const handle_discard = () => set_discard_modal_open(true);
    useEffect(() => {
        function handleClickOutside(event) {
            if (discard_ref.current && !discard_ref.current.contains(event.target)) {
                set_discard_modal_open(false);
            }
        }
        if (discard_modal_open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [discard_modal_open]);

    const breadcrumbs = ["Listing", "Listing & Pricing", "Bulk Pricing"];

    // Platform mapping rows (left unchanged for now)
    const [rows, set_rows] = useState([
        { id: 1, platform: '', product_id: '', sku_id: '', platform_open: false }
    ]);
    const row_refs = useRef({});

    const add_row = () => {
        const newId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
        set_rows(prev => [...prev, { id: newId, platform: '', product_id: '', sku_id: '', platform_open: false }]);
    };
    const delete_row = id => set_rows(prev => prev.filter(r => r.id !== id));
    const update_row = (id, changes) => set_rows(prev => prev.map(r => r.id === id ? { ...r, ...changes } : r));

    // Save modal (existing)
    const save_ref2 = useRef(null);
    const [save_modal_open_2, set_save_modal_open_2] = useState(false);
    const handle_save_confirm_2 = () => set_save_modal_open_2(false);
    useEffect(() => {
        function outside(e) { if (save_ref2.current && !save_ref2.current.contains(e.target)) set_save_modal_open_2(false); }
        if (save_modal_open_2) document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [save_modal_open_2]);

    // select stock dropdown state (kept)
    const select_stock_ref = useRef(null);
    const [select_stock_open, set_select_stock_open] = useState(false);
    const [select_stock_search, set_select_stock_search] = useState("");
    const [form_data, set_form_data] = useState({
        select_stock: "",
        id: "",
        status: "1",
        notes: "",
    });
    const toggle_select_stock = () => set_select_stock_open((s) => !s);

    const STOCK_OPTIONS = [
        { title: "Samsung S25 Plus", sku: "Mint, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Icyblue, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Navy, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Navy, 12GB, 256GB", condition: "Used", status: "Pristine" },
        { title: "Samsung S25 Plus", sku: "Navy, 12GB, 512GB", condition: "Used", status: "Pristine" },
        { title: "Samsung S25 Plus", sku: "Silver Shadow, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Blueblack, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Apple iPhone 16", sku: "Starlight, 8GB, 256GB", condition: "New", status: "Activated" },
        { title: "Apple iPhone 16", sku: "Black, 8GB, 512GB", condition: "Used", status: "Refurbished" },
        { title: "Apple iPhone 15", sku: "Red, 6GB, 128GB", condition: "New", status: "Unactivated" },
        { title: "Apple iPad Air", sku: "Space Gray, 8GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Apple iPad Pro", sku: "Silver, 16GB, 1TB", condition: "Used", status: "Pristine" },
    ];

    useEffect(() => {
        function onDocClick(e) {
            if (select_stock_ref.current && !select_stock_ref.current.contains(e.target)) {
                set_select_stock_open(false);
            }
        }
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const handle_select_stock_select = (opt) => {
        set_form_data((prev) => ({ ...prev, select_stock: opt }));
        set_select_stock_open(false);
        set_select_stock_search("");
    };

    // --- Utilities (kept) ---
    const escapeRegExp = (string) => String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const normalizeForSearch = (s) =>
        String(s || "")
            .replace(/[\u2013\u2014–—\-|\/\\(),:;]+/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();
    const buildTokens = (query) => normalizeForSearch(query).split(" ").filter(Boolean);
    const highlightForTokens = (text, tokens) => {
        if (!tokens || tokens.length === 0) return text;
        const uniqueTokens = Array.from(new Set(tokens)).sort((a, b) => b.length - a.length);
        const safePattern = uniqueTokens.map((t) => escapeRegExp(t)).join("|");
        const regex = new RegExp(`(${safePattern})`, "ig");
        return String(text).split(regex).map((part, i) => {
            if (!part) return null;
            if (regex.test(part)) {
                return (
                    <span key={i} style={{ color: "#0D6EFD" }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    const tokens = buildTokens(select_stock_search);
    const filteredOptions = tokens.length
        ? STOCK_OPTIONS.filter((opt) => {
            const hay = normalizeForSearch(`${opt.title} ${opt.sku} ${opt.condition} ${opt.status}`);
            return tokens.every((tok) => hay.includes(tok));
        })
        : STOCK_OPTIONS;

    const showFinalBg = filteredOptions.length === 1 && tokens.length > 0;
    const PLATFORM_OPTIONS = ['Shopee SG', 'Lazada SG', 'Tik Tok SG'];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (select_stock_open && select_stock_ref.current && !select_stock_ref.current.contains(e.target)) {
                set_select_stock_open(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [select_stock_open]);

    useEffect(() => {
        const handler = (e) => {
            const clickedInPortal = portalRoot && portalRoot.contains(e.target);
            set_rows(prev => prev.map(row => {
                const el = row_refs.current[row.id]?.current;
                if (!clickedInPortal && el && !el.contains(e.target)) {
                    return { ...row, platform_open: false };
                }
                return row;
            }));
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // incomingSelected and pricingRows (kept)
    const incomingSelected = location?.state?.bulkItems ?? null;
    const [selectedForBulk, setSelectedForBulk] = useState(() => {
        if (Array.isArray(incomingSelected) && incomingSelected.length) return incomingSelected;
        return [STOCK_OPTIONS[5], STOCK_OPTIONS[2], STOCK_OPTIONS[3]];
    });
    const [pricingRows, setPricingRows] = useState(() =>
        selectedForBulk.map((it, i) => ({
            id: i + 1,
            title: it.title,
            variant: it.sku,
            condition: it.condition,
            grading: it.status,
            basePrice: '178.00',
            erpPrices: ['1,113.00', '1,113.00', '1,113.00']
        }))
    );
    useEffect(() => {
        setPricingRows(selectedForBulk.map((it, i) => ({
            id: i + 1,
            title: it.title,
            variant: it.sku,
            condition: it.condition,
            grading: it.status,
            basePrice: '178.00',
            erpPrices: ['1,113.00', '1,113.00', '1,113.00']
        })));
    }, [selectedForBulk]);
    const updatePricingRow = (rowId, changes) => {
        setPricingRows(prev => prev.map(r => r.id === rowId ? { ...r, ...changes } : r));
    };

    // Change Price modal and success state
    const changepriceRef = useRef(null);
    const [changepriceModalOpen, setChangepriceModalOpen] = useState(false);
    const [editSuccess, setEditSuccess] = useState(false);

    // when confirm change price, close modal and show success view
    const handleChangepriceConfirm = () => {
        setChangepriceModalOpen(false);
        // set success state -> this will replace the main block with success design
        setEditSuccess(true);
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (changepriceRef.current && !changepriceRef.current.contains(event.target)) {
                setChangepriceModalOpen(false);
            }
        }
        if (changepriceModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [changepriceModalOpen]);

    // navigation handlers for the two buttons in success view
    const handleToListing = () => {
        // push to listing-pricing and include state to indicate which tab to open
        history.push('/listing-pricing', { activeTab: 'listing', fromBulk: true });
    };
    const handleViewPricingHistory = () => {
        // push to listing-pricing with a different tab state (you can adapt keys to your router)
        history.push('/listing-pricing', { activeTab: 'price-history', fromBulk: true });
    };

    return (
        <div className="company-body main-company-body-inner new-look bulk-pricing-root">
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <span
                            key={index}
                            className={`breadcrumb-item ${crumb === "Listing & Pricing" ? "clickable" : ""}`}
                            onClick={() => { if (crumb === "Listing & Pricing") history.push('/listing-pricing'); }}
                            style={{ cursor: crumb === "Listing & Pricing" ? 'pointer' : 'default' }}
                        >
                            {crumb}{!isLast ? '  ' : ''}
                        </span>
                    );
                })}
            </div>

            <div className='edit-organisation-info'>
                <div className='edit-organisation-left'>
                    <h1>Bulk Pricing <Tooltip content="Bulk Pricing"><Info size={16} className="info-icon" /></Tooltip></h1>
                </div>

                <div className='discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2'>
                    {/* Header buttons: when editSuccess is true show "Back to Edit", otherwise show Cancel + Save */}
                    {!editSuccess ? (
                        <>
                            {/* <button className="dark-button" type='button' onClick={handle_discard}>Cancel</button> */}
                            <button className="dark-button" type='button'     onClick={() => setShowWip(true)}>Cancel</button>
                              {/* Work In Progress Modal */}
  <WorkInProgress
    isOpen={showWip}
    onClose={() => setShowWip(false)}
    onConfirm={() => setShowWip(false)}
  />

                            <button className="dark-border" type="button" onClick={() => { setChangepriceModalOpen(true); }}>Save</button>
                        </>
                    ) : (
                        <button className="dark-button" type="button" onClick={() => setEditSuccess(false)}>Back to Edit</button>
                    )}
                </div>
            </div>

            {/* Change Price Modal */}
            {changepriceModalOpen && (
                <>
                    <div className="drawer-overlay" />
                    <div className="entity-drawer modal-popup-cls" ref={changepriceRef}
                        style={{
                            maxWidth: 650,
                            margin: "0 auto",
                            background: "white",
                            borderRadius: 12,
                            zIndex: 1050,
                            padding: 24,
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                            height: "auto",
                            right: "auto",
                            overflow: "inherit",
                        }}>
                        <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                            <button className="close-btn" onClick={() => setChangepriceModalOpen(false)}><X size={20} /></button>
                        </div>

                        <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>Change Price</h3>
                        <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>
                            Please provide a reason for the price change. This helps maintain a clear record for audit and tracking purposes.
                        </p>

                        <div className="drawer-content">
                            <div className="row gappingcls-added">
                                <div className="col-12">
                                    <div className="info-inner tab-panel">
                                        <div className='field-block'>
                                            <div className='field-label' style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                                                Reason <span className="error-color" style={{ color: "red" }}>*</span>
                                                <Tooltip content="Choose Reason"><Info size={16} className="info-icon" /></Tooltip>
                                            </div>
                                            <textarea
                                                className="form-control w-100 common-textarea-all"
                                                id="exampleFormControlTextarea1"
                                                rows="3"
                                                name="Reason"
                                                style={{
                                                    borderColor: '#6c757d',
                                                    borderRadius: '8px',
                                                    resize: 'none',
                                                    height: '120px',
                                                    overflowY: 'auto',
                                                    fontFamily: '"Montserrat", sans-serif',
                                                    color: '#6c757d',
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                    lineHeight: '18px',
                                                    boxShadow: 'none',
                                                }}
                                            ></textarea>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                            <button className="btn-cancel" onClick={() => setChangepriceModalOpen(false)}>Cancel</button>
                            <button className="btn-save" onClick={handleChangepriceConfirm}>Change Price</button>
                        </div>
                    </div>
                </>
            )}

            <div className="employee-tabs-container">
                {save_modal_open_2 && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" ref={save_ref2} style={{ maxWidth: 650, margin: '0 auto', background: 'white', borderRadius: 12, zIndex: 1050, padding: 24, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', height: 'auto', right: 'auto', overflow: 'inherit' }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => set_save_modal_open_2(false)}><X size={20} /></button>
                            </div>
                            <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: '24px', fontFamily: 'Montserrat', marginBottom: '5px' }}>The edit function implementation</h3>
                            <div className="drawer-footer" style={{ paddingTop: '10px' }}>
                                <button className="btn-save" onClick={handle_save_confirm_2}>OK</button>
                            </div>
                        </div>
                    </>
                )}

                <div className="tab-content" id="edit-spu-content">
                    <div className="info-section">
                        {/* If editSuccess is true, show the final success card (screenshot design). Otherwise show the edit pricing table. */}
                        {editSuccess ? (
                            <div className="info-card">
                                <div className="info-content d-block entities-tab">
                                    <div className="success-visual" style={{ textAlign: 'center', marginBottom: 10 }}>
                                        <div className="success-icon" aria-hidden style={{
                                            width: 32, height: 32, borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FFEB00', border: '2px solid #212529'
                                        }}>
                                            <svg width="16" height="11" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 2L7 13L2 8" stroke="black" stroke-width="2.66667" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </div>
                                    </div>

                                    <h2 style={{ textAlign: 'center', margin: 0, fontFamily: 'Montserrat', fontSize: 20, lineHeight: '24px', fontWeight: 600, color: '#212529' }}>Edit Success</h2>

                                    <p style={{ textAlign: 'center', marginTop: 10, marginBottom: 20, color: '#6C757D' }}>
                                        The following prices are now reflected on the respective eCommerce Platforms
                                    </p>

                                    <div className='edit-organisation-info' style={{ display: 'flex', justifyContent: 'center', gap: 10, paddingTop: 10, paddingBottom: 10, flexWrap: 'wrap', margin: 0, boxShadow: 'none', borderRadius: '0', backgroundColor: 'transparent' }}>
                                        <button className="dark-button" onClick={handleViewPricingHistory}>View Pricing History</button>
                                        <button className="dark-border" onClick={handleToListing}>To Listing</button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="info-card new-info-overflow-auto main-company-body-inner">
                                <div className="info-header"><h4 className="info-title">Edit Pricing ({selectedForBulk.length})</h4></div>
                                <div className="info-content d-block entities-tab">
                                    <div style={{ padding: '20px', boxShadow: '0px 1px 4px #0000001A', borderRadius: '12px' }}>
                                        <div className="table-wrapper" style={{ position: 'relative', overflowX: 'auto' }}>
                                            <table className="entities-table tab-table w-100">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: '50%' }}><span>Stock</span></th>
                                                        <th style={{ width: '20%' }}><span>Base Price</span></th>
                                                        <th style={{ width: '10%' }}><span>Platform</span></th>
                                                        <th style={{ width: '20%' }}><span>ERP Price</span></th>
                                                    </tr>
                                                </thead>

                                                <tbody>
                                                    {pricingRows.map((row, idx) => (
                                                        <PriceRow
                                                            key={row.id}
                                                            row={row}
                                                            rowIndex={idx}
                                                            updateRow={(changes) => updatePricingRow(row.id, changes)}
                                                        />
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function PriceRow({ row, rowIndex, updateRow }) {
    const platforms = ['Shopee SG', 'Lazada SG', 'TikTok SG'];

    const onBaseChange = (e) => {
        updateRow({ basePrice: e.target.value });
    };

    const onErpChange = (idx, val) => {
        const next = [...row.erpPrices];
        next[idx] = val;
        updateRow({ erpPrices: next });
    };

    return (
        <tr style={{ background: rowIndex % 2 === 1 ? '#F8F9FB' : 'transparent' }}>
            <td style={{ verticalAlign: 'middle' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                        <img src="https://rukminim2.flixcart.com/image/704/844/xif0q/mobile/3/5/l/-original-imaghx9qmgqsk9s4.jpeg?q=90&crop=false" alt="" style={{ width: 36, height: 36, borderRadius: 6 }} />
                    </div>

                    <div>
                        <div>{row.title}</div>
                        <div style={{ marginTop: 4, fontSize: 10, lineHeight: '12px', color: '#6C757D' }}>{shortVariant(row.variant)}</div>

                        <div style={{ marginTop: 4 }}>
                            <span style={{ background: '#D1E7DD', color: '#198754', borderRadius: 999, border: '1px solid #198754', padding: '2px 8px', fontSize: 8, lineHeight: '10px', fontWeight: 600, marginRight: 5 }}>New</span>
                            <span style={{ fontSize: 10, lineHeight: '12px', color: '#6C757D' }}>{row.grading}</span>
                        </div>
                    </div>
                </div>
            </td>

            <td style={{ verticalAlign: 'middle' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #6C757D', borderRadius: 8, minWidth: 210, overflow: 'hidden' }}>
                    <div style={{ color: '#6C757D', fontWeight: 400, fontSize: 14, padding: '10px 14px' }}>SGD</div>
                    <input
                        value={row.basePrice}
                        onChange={onBaseChange}
                        className="form-control"
                        style={{ border: 'none', outline: 'none', boxShadow: 'none', minWidth: 150, textAlign: 'left', borderLeft: '1px solid #6C757D', borderRadius: 0 }}
                    />
                </div>
            </td>

            <td style={{ verticalAlign: 'middle' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {platforms.map((p) => (
                        <div key={p} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                <circle cx="4" cy="4" r="3" fill="#198754" />
                            </svg>
                            <span style={{ color: '#212529', whiteSpace: 'nowrap' }}>{p}</span>
                        </div>
                    ))}
                </div>
            </td>

            <td style={{ verticalAlign: 'middle' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
                    {row.erpPrices.map((val, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #6C757D', borderRadius: 8, minWidth: 210, overflow: 'hidden' }}>
                                <div style={{ color: '#6C757D', fontWeight: 400, fontSize: 14, padding: '10px 14px' }}>SGD</div>
                                <input
                                    value={val}
                                    onChange={(e) => onErpChange(i, e.target.value)}
                                    className="form-control"
                                    style={{ border: 'none', outline: 'none', boxShadow: 'none', minWidth: 150, textAlign: 'left', borderLeft: '1px solid #6C757D', borderRadius: 0 }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </td>
        </tr>
    );
}
